import React, { useState } from 'react';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { MuiBox, Grid, Tooltip, Typography } from '@kis-technologies/kis-ui';
import { KIOSK_URL } from 'services/api/base';
import { selectors as authSelectors } from 'store/auth';
import { actions as kioskActions, selectors as kioskSelectors, Kiosk } from 'store/kiosks';
import RebootIcon from 'assets/rebootIcon.jpg';
import ReloadIcon from 'assets/reloadIcon.jpg';
import ScheduleIcon from 'assets/scheduleIcon.jpg';
import KioskPreview from 'assets/previewIcon.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { KioskScheduleDrawer } from './KioskSchedule';
import { KioskRestockDrawer } from './KioskRestock';
import { useStyles } from '../styled';
import get from 'lodash.get';

export interface KioskActionsProps {
  kiosk: Kiosk;
}

const KioskActions = (props: KioskActionsProps) => {
  const { kiosk } = props;
  const { rebootKiosk, createKioskLoginToken, resetLoginToken, getKioskSchedule } = useActions({
    rebootKiosk: kioskActions.rebootKiosk,
    createKioskLoginToken: kioskActions.createKioskLoginToken,
    resetLoginToken: kioskActions.resetLoginToken,
    getKioskSchedule: kioskActions.getKioskSchedule
  });

  const admin = useSelector(authSelectors.selectAdmin);
  const loginToken = useSelector(kioskSelectors.selectKioskLoginToken);
  const previewKiosk = get(kiosk, 'configuration.kioskPreview.enabled');
  const [showKioskSchedule, setShowKioskSchedule] = useState(false);
  const [showRebootConfirmation, setShowRebootConfirmation] = useState(false);
  const [showTicketStockOverlay, setshowTicketStockOverlay] = React.useState(false);
  const classes = useStyles()();

  React.useEffect(() => {
    if (kiosk && loginToken && loginToken[kiosk.id]) {
      window.open(`${KIOSK_URL}?id=${btoa(kiosk.id)}&key=${loginToken[kiosk.id]}`);
      resetLoginToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginToken]);

  const onButtonClick = (value: string) => {
    if (value === 'schedule') {
      getKioskSchedule({ kioskId: kiosk.id });
      setShowKioskSchedule(true);
    } else if (value === 'reboot') {
      setShowRebootConfirmation(true);
    } else {
      setshowTicketStockOverlay(true);
    }
  };

  const onScheduleClose = () => {
    setShowKioskSchedule(false);
  };

  const onRebootKioskConfirmClick = () => {
    setShowRebootConfirmation(false);
    rebootKiosk({ kioskId: kiosk.id, adminId: admin ? admin.id : '' });
  };

  const handlePreviewClick = () => {
    if (kiosk) {
      createKioskLoginToken({ kioskId: kiosk.id, adminId: admin ? admin.id : '' });
    }
  };

  return (
    <>
      {showRebootConfirmation && (
        <ActionOverlay
          title={
            <MuiBox className={classes.kioskRebootHeading}>
              <Typography className={classes.kioskRebootHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
                Are you sure you want to reboot {kiosk.name}?
              </Typography>
            </MuiBox>
          }
          description={
            <MuiBox className={classes.kioskRebootDescription} width={600}>
              <Typography className={classes.kioskRebootDescriptionText}>
                {kiosk.name} will reboot immediately
              </Typography>
            </MuiBox>
          }
          confirmText="Reboot Now"
          onConfirm={onRebootKioskConfirmClick}
          cancelText="Back"
          onCancel={() => setShowRebootConfirmation(false)}
          width={800}
          appearance="error"
        />
      )}
      <Grid className={classes.KioskActionsButtons}>
        <Tooltip title="Reload Ticket Stock" arrow placement="top" verticalOffset={-8}>
          <img
            src={ReloadIcon}
            alt="Reload Icon"
            onClick={() => onButtonClick('reload')}
            data-testid="kiosk-restock-button"
          />
        </Tooltip>
        <Tooltip title="Kiosk Schedule" arrow placement="top" verticalOffset={-8}>
          <img
            src={ScheduleIcon}
            alt="Schedule Icon"
            onClick={() => onButtonClick('schedule')}
            data-testid="kiosk-schedule-button"
          />
        </Tooltip>
        <Tooltip title="Reboot Kiosk" arrow placement="top" verticalOffset={-8}>
          <img
            src={RebootIcon}
            alt="Reboot Icon"
            style={kiosk.online ? { opacity: 1 } : { opacity: 0.5, cursor: 'not-allowed' }}
            onClick={() => kiosk.online && onButtonClick('reboot')}
            data-testid="kiosk-reboot-button"
          />
        </Tooltip>
        {previewKiosk && (
          <Tooltip title="Preview Kiosk" arrow placement="top" verticalOffset={-8}>
            <img
              src={KioskPreview}
              alt="Preview Icon"
              onClick={() => handlePreviewClick()}
              data-testid="kiosk-preview-button"
            />
          </Tooltip>
        )}
      </Grid>
      {showKioskSchedule && <KioskScheduleDrawer onClose={onScheduleClose} isOpen={showKioskSchedule} kiosk={kiosk} />}
      {showTicketStockOverlay && (
        <KioskRestockDrawer
          isOpen={showTicketStockOverlay}
          onClose={() => setshowTicketStockOverlay(false)}
          kiosk={kiosk}
        />
      )}
    </>
  );
};

export default KioskActions;
